// PlatformDashboard — platform-admin home page at /platform.
// CHG-1540 / ITER-3658 IA correction.
//
// Replaces the broken UX where a platform admin with no active org
// landed at "/" and saw the org Overview render with empty stats (no
// active tenant context). Platform admins now land here by default
// (RoleAwareRedirect) and see real cross-tenant aggregates from
// /api/v1/platform/* endpoints.
//
// Slots:
//   - StatStrip: Organizations / Platform members / v1 weekly active /
//     v2 weekly active (4 cards)
//   - feed: deep link to /platform/v2-adoption (the CHG-1307
//     V2AdoptionDashboard with time-series + funnel detail) +
//     placeholders for the 5 forwardPromised sibling pages
//
// Auth: ProtectedRoute(no requirePermission). Platform-admin gating
// happens at the BE for every /platform/* call (each returns 403 to
// non-platform-admin). The Sidebar Platform group is gated on
// account.is_platform_admin (CHG-1523); if a non-platform-admin
// reaches this page directly they'll see API 403s + the DashboardPage
// error retry card.

import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { DashboardPage } from "../components/composition/DashboardPage";
import { StatStrip, type StatStripItem } from "../components/composition/StatStrip";
import { platformApi, type PlatformOverview } from "../api/platform";

function statsToItems(data: PlatformOverview | undefined): StatStripItem[] {
  return [
    {
      id: "orgs",
      label: "Organizations",
      value: data?.org_count,
      format: "number",
      tone: "g-platform",
    },
    {
      id: "members",
      label: "Platform members",
      value: data?.members.total,
      format: "number",
      tone: "g-platform",
    },
    {
      id: "v1-wa",
      label: "v1 weekly active",
      value: data?.adoption.v1_weekly_active,
      format: "number",
      tone: "g-channels",
    },
    {
      id: "v2-wa",
      label: "v2 weekly active",
      value: data?.adoption.v2_weekly_active,
      format: "number",
      tone: "g-giving",
    },
  ];
}

// Inline feed card — placeholder links to the platform-admin surfaces.
// Same SOON-style pattern as the donor portal RecentDonationsCard +
// the Platform nav group items themselves (CHG-1523). When the dedicated
// v2 pages land (organizations / members / activity / audit-logs /
// billing) each row swaps from a SOON badge to a real link.
function PlatformLinksCard({
  hasAdoption,
}: {
  hasAdoption: boolean;
}) {
  return (
    <section
      className="card"
      aria-labelledby="platform-links-title"
      style={{ padding: 0, display: "flex", flexDirection: "column" }}
    >
      <header
        style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          padding: "14px 16px",
          borderBottom: "1px solid var(--color-border-subtle)",
        }}
      >
        <h2
          id="platform-links-title"
          style={{ fontSize: 14, fontWeight: 500, margin: 0, color: "var(--color-text)" }}
        >
          Platform surfaces
        </h2>
      </header>
      <ul role="list" style={{ listStyle: "none", margin: 0, padding: 0 }}>
        {/* Real link: V2AdoptionDashboard (CHG-1307). */}
        <li
          style={{
            display: "grid",
            gridTemplateColumns: "1fr auto",
            gap: 8,
            padding: "12px 16px",
            borderBottom: "1px solid var(--color-border-subtle)",
            alignItems: "center",
          }}
        >
          <div>
            <div style={{ fontSize: 13, fontWeight: 500 }}>v2 Adoption</div>
            <div style={{ fontSize: 12, color: "var(--color-text-muted)" }}>
              Trailing 7d stat strip, 30d time series, banner→welcome funnel.
            </div>
          </div>
          <Link
            to="/platform/v2-adoption"
            data-testid="platform-link-v2-adoption"
            style={{ fontSize: 13, color: "var(--color-text)" }}
          >
            Open →
          </Link>
        </li>
        {/* SOON rows for the 5 forwardPromised Platform group items. */}
        {[
          { label: "Organizations", desc: "All tenants across the platform.", route: "/platform/organizations" },
          { label: "Members", desc: "Platform-admin and org-admin accounts.", route: "/platform/members" },
          { label: "Activity", desc: "Cross-tenant activity feed.", route: "/platform/activity" },
          { label: "Audit logs", desc: "Sensitive-action audit trail.", route: "/platform/audit-logs" },
          { label: "Billing", desc: "Subscription + invoice oversight.", route: "/platform/billing" },
        ].map((row) => (
          <li
            key={row.route}
            style={{
              display: "grid",
              gridTemplateColumns: "1fr auto",
              gap: 8,
              padding: "12px 16px",
              borderBottom: "1px solid var(--color-border-subtle)",
              alignItems: "center",
            }}
          >
            <div>
              <div style={{ fontSize: 13, fontWeight: 500 }}>{row.label}</div>
              <div style={{ fontSize: 12, color: "var(--color-text-muted)" }}>{row.desc}</div>
            </div>
            <span
              data-testid={`platform-link-soon-${row.label.toLowerCase().replace(/\s+/g, "-")}`}
              style={{
                fontSize: 11,
                fontWeight: 600,
                padding: "2px 8px",
                borderRadius: 9999,
                background: "var(--color-bg-subtle)",
                color: "var(--color-text-muted)",
              }}
            >
              SOON
            </span>
          </li>
        ))}
      </ul>
      {!hasAdoption ? (
        <p
          style={{
            margin: 0,
            padding: "10px 16px",
            fontSize: 12,
            color: "var(--color-text-muted)",
          }}
        >
          Adoption telemetry is empty so far — the v1/v2 active counts will populate as users sign in.
        </p>
      ) : null}
    </section>
  );
}

export function PlatformDashboard() {
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ["platform-overview"],
    queryFn: platformApi.fetchPlatformOverview,
  });

  const items = statsToItems(data);
  const hasAdoption =
    (data?.adoption.v1_weekly_active ?? 0) > 0 ||
    (data?.adoption.v2_weekly_active ?? 0) > 0;

  return (
    <DashboardPage
      title="Platform admin"
      subtitle="Cross-tenant overview. Manage organizations, members, billing, and adoption from one place."
      stats={<StatStrip items={items} loading={isLoading} />}
      feed={<PlatformLinksCard hasAdoption={hasAdoption} />}
      loading={isLoading}
      error={error as Error | null}
      onRetry={() => refetch()}
    />
  );
}
